/**
 * Type-safe localStorage wrapper with a transparent native mirror for keys
 * we want to survive the iOS WKWebView's tendency to evict localStorage
 * between cold launches.
 *
 * Two mirror classes, backed by two different native stores:
 *   - SECURE_KEYS — sensitive (JWT, refresh token). Their loss would log the
 *     user out, and their *exposure* would be an account-takeover risk, so
 *     these are mirrored through `@capgo/capacitor-native-biometric`'s
 *     `setCredentials`/`getCredentials`/`deleteCredentials` — genuinely
 *     Keychain-backed on iOS and AndroidKeyStore-encrypted on Android (see
 *     `auth/refreshTokenStorage.ts` for the same pattern used for the
 *     refresh token itself).
 *   - MIRROR_KEYS / MIRROR_KEY_SUFFIXES — non-secret UX preferences (theme,
 *     etc.) mirrored to {@link Preferences} (plain `UserDefaults` on iOS,
 *     plain `SharedPreferences` on Android, a localStorage proxy on web).
 *     Losing them isn't a security event but is annoying — e.g. dark mode
 *     resetting to system every launch — and Preferences' lack of
 *     encryption doesn't matter for values like "dark" vs "light".
 *     `@capacitor/preferences` must NOT be used for anything in SECURE_KEYS.
 *
 * The public API stays synchronous so call sites — including the axios
 * request interceptor that runs on every API call — don't need to await.
 * Internally, for matching keys, every write is mirrored asynchronously to
 * the appropriate native store above.
 *
 * Reads always come from localStorage. On native, {@link hydrateSecureStorage}
 * (called from main.tsx before render) repopulates localStorage from the
 * native mirrors at app startup — so a WKWebView eviction between launches
 * doesn't reset state.
 *
 * Web behaviour is unchanged: both mirrors are no-ops on web (Preferences
 * proxies to localStorage; the secure-credential mirror is gated on
 * `Capacitor.isNativePlatform()`), so the redundant writes are harmless.
 */

import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { getKeychainServer } from '../auth/biometricAuth';

/**
 * Sensitive keys mirrored to platform secure storage (Keychain / Keystore,
 * NOT Preferences). Loss of these would log the user out; exposure of these
 * would be an account-takeover risk. Add new ones sparingly — the mirror has
 * a cost on every write, and each one gets its own Keychain/Keystore entry.
 */
const SECURE_KEYS: ReadonlySet<string> = new Set([
  'jwt_token',
  'refresh_token',
]);

/**
 * Non-secret preference keys we still want mirrored so iOS WKWebView
 * localStorage eviction doesn't reset the user's UX preferences between
 * cold launches.
 */
const MIRROR_KEYS: ReadonlySet<string> = new Set([
  'theme_preference',
]);

/**
 * Dynamic key suffixes for keys whose full name isn't known up-front
 * (per-tenant preferences). Matched with {@link String.endsWith}. Hydrate
 * enumerates Preferences keys at startup to find these.
 */
const MIRROR_KEY_SUFFIXES: ReadonlyArray<string> = [
  '_theme_preference', // matches getTenantThemeKey(tenantId)
];

// NativeBiometric's Credentials API stores one username/password pair per
// `server` string — it's not a general key-value store — so each secure key
// gets its own server string (namespaced under this app's keychain scope,
// distinct from both the biometric sentinel and the refresh-token credential
// so none of the three ever collide) and a fixed placeholder username; the
// mirrored value itself is the `password` field.
const SECURE_STORE_USERNAME = 'value';

function secureServer(key: string): string {
  return `${getKeychainServer()}.storage.${key}`;
}

function shouldMirrorSecure(key: string): boolean {
  return SECURE_KEYS.has(key);
}

function shouldMirrorPreference(key: string): boolean {
  if (MIRROR_KEYS.has(key)) return true;
  return MIRROR_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix));
}

function mirrorSet(key: string, value: string): void {
  // Fire-and-forget: a failed mirror is logged but doesn't surface to the
  // caller. The localStorage write already succeeded, so we just lose the
  // durability benefit if this particular write fails.
  if (shouldMirrorSecure(key)) {
    void NativeBiometric.setCredentials({
      username: SECURE_STORE_USERNAME,
      password: value,
      server: secureServer(key),
    }).catch((err) => {
      console.warn(`secure storage mirror set(${key}) failed:`, err);
    });
    return;
  }
  if (shouldMirrorPreference(key)) {
    void Preferences.set({ key, value }).catch((err) => {
      console.warn(`secure storage mirror set(${key}) failed:`, err);
    });
  }
}

function mirrorRemove(key: string): void {
  if (shouldMirrorSecure(key)) {
    void NativeBiometric.deleteCredentials({ server: secureServer(key) }).catch((err) => {
      console.warn(`secure storage mirror remove(${key}) failed:`, err);
    });
    return;
  }
  if (shouldMirrorPreference(key)) {
    void Preferences.remove({ key }).catch((err) => {
      console.warn(`secure storage mirror remove(${key}) failed:`, err);
    });
  }
}

export const storage = {
  /**
   * Get item from localStorage with type safety
   */
  get<T>(key: string, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },

  /**
   * Set item in localStorage
   */
  set<T>(key: string, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
      mirrorSet(key, serialized);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  },

  /**
   * Remove item from localStorage
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
      mirrorRemove(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  },

  /**
   * Clear all items from localStorage
   */
  clear(): void {
    try {
      localStorage.clear();
      // Also wipe the mirror — clear() means a deliberate full reset (logout +
      // teardown), so we don't want stale data surviving in Preferences.
      // Dynamic suffix-matched keys are not enumerated here; targeted
      // storage.remove() calls in production handle real teardown.
      for (const key of SECURE_KEYS) mirrorRemove(key);
      for (const key of MIRROR_KEYS) mirrorRemove(key);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  },
};

/**
 * Repopulate localStorage from the platform store at app startup. Call
 * once, before rendering the app, from native shells only.
 *
 * Covers three classes of keys, from two different native stores:
 *   1. SECURE_KEYS — exact-match (jwt, refresh) — read from the Keychain /
 *      Keystore-backed credential store via NativeBiometric.getCredentials.
 *   2. MIRROR_KEYS — exact-match non-secret prefs (global theme) — read from
 *      Preferences.
 *   3. MIRROR_KEY_SUFFIXES — dynamic keys discovered via Preferences.keys()
 *      (per-tenant theme).
 *
 * No-op on web (both native stores are gated on isNativePlatform() /
 * Preferences proxies to localStorage, so the round-trip is wasted but
 * harmless). Per-key no-op when localStorage already has a value (current
 * session was preserved cleanly).
 *
 * Note: the function name is historical — it now hydrates both secure
 * and non-secret mirror keys. Main entry caller is in main.tsx.
 */
export async function hydrateSecureStorage(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // SECURE_KEYS live in the Keychain/Keystore-backed credential store, not
  // Preferences. getCredentials rejects when nothing is stored yet for that
  // key, which is the normal "never set on this device" state, so we stay
  // quiet on that failure rather than warning.
  for (const key of SECURE_KEYS) {
    if (localStorage.getItem(key) !== null) continue;
    try {
      const { password } = await NativeBiometric.getCredentials({ server: secureServer(key) });
      if (password) localStorage.setItem(key, password);
    } catch {
      // Not stored yet — nothing to hydrate.
    }
  }

  // MIRROR_KEYS (exact-match, non-secret prefs) live in Preferences.
  for (const key of MIRROR_KEYS) {
    if (localStorage.getItem(key) !== null) continue;
    try {
      const { value } = await Preferences.get({ key });
      if (value !== null) localStorage.setItem(key, value);
    } catch (err) {
      console.warn(`secure storage hydrate(${key}) failed:`, err);
    }
  }

  // Dynamic suffix-matched keys (per-tenant prefs). Enumerate Preferences
  // keys once and pull any that match a registered suffix and aren't already
  // present in localStorage.
  if (MIRROR_KEY_SUFFIXES.length === 0) return;
  try {
    const { keys } = await Preferences.keys();
    for (const key of keys) {
      if (!MIRROR_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix))) continue;
      if (localStorage.getItem(key) !== null) continue;
      try {
        const { value } = await Preferences.get({ key });
        if (value !== null) localStorage.setItem(key, value);
      } catch (err) {
        console.warn(`secure storage hydrate(${key}) failed:`, err);
      }
    }
  } catch (err) {
    console.warn('secure storage hydrate: suffix scan failed:', err);
  }
}

/**
 * Storage keys used throughout the app
 */
export const STORAGE_KEYS = {
  JWT_TOKEN: 'jwt_token',
  REFRESH_TOKEN: 'refresh_token',
  THEME_PREFERENCE: 'theme_preference',
  REMEMBER_ME: 'remember_me',
  SERVICE_AREA_MAP_VIEW: 'service_area_map_view',
  MAP_SATELLITE_VIEW: 'map_satellite_view',
} as const;

/**
 * Get theme preference key for a specific tenant
 */
export function getTenantThemeKey(tenantId: string): string {
  return `${tenantId}_theme_preference`;
}
